import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Ban,
  BookOpen,
  Building2,
  Check,
  CircleDollarSign,
  ClipboardCopy,
  Download,
  Crown,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Flag,
  Globe2,
  Languages,
  LayoutDashboard,
  Link as LinkIcon,
  ListChecks,
  Lock,
  Mail,
  MessageCircle,
  PlugZap,
  QrCode,
  ReceiptText,
  Rocket,
  Search,
  Send,
  Share2,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Store,
  User,
  UserPlus,
  Users,
  WalletCards,
  X
} from "lucide-react";
import { lazy, Suspense, type FormEvent, type Ref, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { blogPosts } from "./blog";
import { categories, districts } from "./data";
import {
  copyText,
  apiJson,
  downloadBrandedQrPng,
  downloadCsv,
  downloadQrPng,
  fileToDataUrl,
  getBusinesses,
  getReports,
  getRequests,
  maskFps,
  money,
  saveBusinesses,
  saveReports,
  saveRequests,
  siteOrigin,
  slugify,
  toQrDataUrl,
  verificationLabel
} from "./lib";
import { getToolBySlug } from "./tools/catalog";
import {
  AUTH_INTENT_KEY,
  GUEST_DRAFT_KEY,
  PENDING_REQUEST_KEY,
  clearLocalValue,
  readAuthIntent,
  readGuestDraft,
  readPendingRequestDraft,
  saveAuthIntent,
  saveGuestDraft,
  savePendingRequestDraft,
  type AuthIntent,
  type PaymentRequestDraft
} from "./tools/drafts";
import type { AuthUser, BusinessProfile, Currency, ErpIntegration, PaymentRequest, ProxyType, ReportRecord, RequestStatus } from "./types";

const ToolRoute = lazy(() => import("./tools/ToolRoute"));

const nav = [
  { href: "/tools/", label: "Tools" },
  { href: "/pricing", label: "Pricing" },
  { href: "/faq", label: "FAQ" },
  { href: "/blog", label: "Blog" }
];

type AuthorProfile = {
  name: string;
  role: string;
  bio: string;
  avatar: string;
};

const authors: AuthorProfile[] = [
  {
    name: "Robert Ruiz",
    role: "Payment operations writer",
    bio: "Robert has worked with small business payment operations, invoice follow-up, and reconciliation workflows for service teams.",
    avatar: "/avatars/author-robert-ruiz.jpg"
  },
  {
    name: "Arron Nichols",
    role: "SME systems writer",
    bio: "Arron has worked with retail and SME systems teams on payment references, customer handoff, and back-office reporting.",
    avatar: "/avatars/author-arron-nichols.jpg"
  },
  {
    name: "Arron Jacobs",
    role: "Commerce workflow writer",
    bio: "Arron has worked in ecommerce operations where payment links, order notes, and finance handoff need to stay simple.",
    avatar: "/avatars/author-arron-jacobs.jpg"
  },
  {
    name: "Wesley Hill",
    role: "Invoice workflow writer",
    bio: "Wesley has worked with finance and admin teams on invoice references, payment reminders, and spreadsheet-based reconciliation.",
    avatar: "/avatars/author-wesley-hill.jpg"
  },
  {
    name: "Meghan Nelson",
    role: "Retail payments writer",
    bio: "Meghan has worked with shop operations and customer service teams that need clearer payment instructions and fewer manual checks.",
    avatar: "/avatars/author-meghan-nelson.jpg"
  },
  {
    name: "Kristina Williams",
    role: "Service business writer",
    bio: "Kristina has worked with service businesses on booking deposits, balance collection, and customer payment communication.",
    avatar: "/avatars/author-kristina-williams.jpg"
  },
  {
    name: "Maya Moreno",
    role: "Operations content writer",
    bio: "Maya has worked in operations content for teams that use payment pages, links, and structured references in daily work.",
    avatar: "/avatars/author-maya-moreno.jpg"
  },
  {
    name: "Danielle Ortiz",
    role: "Business workflow writer",
    bio: "Danielle has worked with business workflow teams on handoff between customer-facing payment instructions and internal records.",
    avatar: "/avatars/author-danielle-ortiz.jpg"
  }
];

type MerchantStory = {
  name: string;
  role: string;
  quote: string;
  avatar?: string;
};

const merchantStories: MerchantStory[] = [
  { name: "Billy M.", role: "Florist owner", quote: "The static profile QR is easy to print, and the dynamic links keep deposit references clean.", avatar: "/avatars/profile-billy-morrison.jpg" },
  { name: "Kurt A.", role: "Repair shop manager", quote: "Customers stopped asking which phone number to pay. The page shows the exact FPS detail and reference.", avatar: "/avatars/profile-kurt-alvarez.jpg" },
  { name: "Soham C.", role: "Tutoring admin", quote: "Payment links with student names and invoice numbers made month-end checking calmer.", avatar: "/avatars/profile-soham-cook.jpg" },
  { name: "Louis J.", role: "Events coordinator", quote: "We send a dynamic link for each booking deposit instead of typing payment instructions every time.", avatar: "/avatars/profile-louis-jones.jpg" },
  { name: "Logan R.", role: "Wholesale operator", quote: "The QR download works for invoices, counter signs, and WhatsApp messages without a payment gateway project.", avatar: "/avatars/profile-logan-riley.jpg" },
  { name: "Ernest R.", role: "Studio owner", quote: "It gives staff one place to copy links, generate QR images, and mark requests after checking the bank.", avatar: "/avatars/profile-eugene-gardner.jpg" },
  { name: "Dustin S.", role: "Freelance designer", quote: "For one-off balances, the amount and reference already being on the link saves a lot of back-and-forth.", avatar: "/avatars/profile-dustin-stewart.jpg" },
  { name: "Randy L.", role: "Club treasurer", quote: "Members scan one page, check the recipient name, and add the right reference.", avatar: "/avatars/profile-randy-long.jpg" },
  { name: "Richard M.", role: "Operations lead", quote: "The webhook and CSV approach fits the way we already send finance data into back-office tools.", avatar: "/avatars/profile-richard-martinez.jpg" },
  { name: "Robin D.", role: "Bakery owner", quote: "Preorder deposits are easier because each product link can carry its own note.", avatar: "/avatars/profile-robin-duncan.jpg" },
  { name: "Rose J.", role: "Boutique manager", quote: "The branded QR option makes payment cards feel more polished when we send quotes.", avatar: "/avatars/profile-rose-jacobs.jpg" },
  { name: "Diane G.", role: "Bookkeeper", quote: "References are much easier to match when the request link already tells the customer what to enter.", avatar: "/avatars/profile-diane-garrett.jpg" },
  { name: "Sofia J.", role: "Pet grooming owner", quote: "I can create a booking deposit link while chatting with a customer.", avatar: "/avatars/profile-sofia-jenkins.jpg" },
  { name: "Angie W.", role: "Training studio admin", quote: "The page keeps payment instructions separate from long chat threads.", avatar: "/avatars/profile-michelle-hall.jpg" },
  { name: "Mia G.", role: "Jewelry shop partner", quote: "For higher value deposits, the reminder to check recipient details is useful.", avatar: "/avatars/profile-mia-gardner.jpg" },
  { name: "Eva H.", role: "Freelance consultant", quote: "I use the profile link on invoices and dynamic links for milestone payments.", avatar: "/avatars/profile-eva-hernandez.jpg" },
  { name: "Victoria W.", role: "Social seller", quote: "It looks more professional than sending a bare FPS ID screenshot.", avatar: "/avatars/profile-victoria-washington.jpg" },
  { name: "Mandy J.", role: "Membership admin", quote: "Recurring dues are easier when every member can receive a link with the right reference.", avatar: "/avatars/profile-mandy-johnson.jpg" },
  { name: "Claire P.", role: "Cafe operator", quote: "We print one static QR by the counter and create dynamic links for catering orders." },
  { name: "Matthew B.", role: "Invoice clerk", quote: "The export format gives our accountant the context they need without digging through messages." },
  { name: "Helen S.", role: "Clinic admin", quote: "Payment instructions are clearer for deposits and follow-up balances." },
  { name: "Andrew T.", role: "Shop owner", quote: "I like that the customer still pays directly to us, with no extra transaction cut." },
  { name: "Laura H.", role: "Education center manager", quote: "Student references are less messy when the payment link carries the class and invoice note." },
  { name: "Peter C.", role: "Logistics coordinator", quote: "Bulk links are useful when several invoices go out at the same time." },
  { name: "Natalie R.", role: "Wedding vendor", quote: "Deposit and balance links help us separate orders without building a checkout flow." },
  { name: "James F.", role: "Service desk lead", quote: "Staff can generate links from one place instead of improvising payment text." },
  { name: "Amy K.", role: "Fitness studio owner", quote: "QR downloads make class packs and one-off promotions easier to share." },
  { name: "Daniel W.", role: "Finance assistant", quote: "The payment page makes the expected recipient name obvious before customers pay." },
  { name: "Olivia N.", role: "Retail operator", quote: "It helps us look organized while keeping FPS direct and simple." },
  { name: "George H.", role: "Workshop owner", quote: "Repair deposits are easier when every link has its own reference." },
  { name: "Emma L.", role: "Admin manager", quote: "The dashboard brings profile QR, payment links, and request status into one place." },
  { name: "Brian D.", role: "Market seller", quote: "I can send a payment page from my phone and keep the customer instructions consistent." },
  { name: "Rachel M.", role: "Studio coordinator", quote: "Dynamic links are useful for promotions because the offer note travels with the QR." },
  { name: "Patrick G.", role: "Wholesale sales", quote: "It reduces payment mistakes when customers have amount, reference, and recipient details together." },
  { name: "Julia F.", role: "Bookstore owner", quote: "The profile URL is now the payment link we put on invoices and receipts." },
  { name: "Sean R.", role: "Agency operator", quote: "Milestone payment links are faster than building a small payment page for every client." },
  { name: "Grace W.", role: "Nonprofit admin", quote: "Donation references are easier to collect when supporters scan a page with instructions." },
  { name: "Kevin P.", role: "Maintenance contractor", quote: "The static QR works on printed job sheets, and dynamic links work for follow-up invoices." },
  { name: "Megan A.", role: "Boutique founder", quote: "It gives our payment instructions a proper home without adding checkout fees." },
  { name: "Simon J.", role: "Club secretary", quote: "CSV export and marked-paid status make manual reconciliation easier." },
  { name: "Nora B.", role: "Home service owner", quote: "Customers can scan, read, and check details before paying." },
  { name: "Adam K.", role: "Operations analyst", quote: "The webhook approach is flexible enough for teams that already use integration tools." },
  { name: "Lily S.", role: "Course provider", quote: "Payment links with expiry dates help us keep enrollment follow-up tidy." },
  { name: "Mark E.", role: "Small manufacturer", quote: "Invoice deposits and balance payments are easier to track with reference-specific links." },
  { name: "Hannah V.", role: "Shop admin", quote: "The page replaces a lot of repeated copy-paste payment instructions." }
];

function authorForSlug(slug: string) {
  const total = slug.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return authors[total % authors.length];
}

function pageMeta(pathname: string) {
  const path = pathname.replace(/\/$/, "") || "/";
  const blogMatch = path.match(/^\/blog\/([^/]+)$/);
  const post = blogMatch ? blogPosts.find((item) => item.slug === blogMatch[1]) : null;
  if (post) return { title: `${post.title} | Lucaden`, description: post.description };
  if (path === "/tools") {
    return {
      title: "Free Payment, Invoice, QR and Business Tools | Lucaden",
      description: "Browse focused Lucaden tools for invoices, client workflows, FPS payment profiles, payment-page QR codes, barcodes, and dynamic payment requests."
    };
  }
  const toolMatch = path.match(/^\/([^/]+)$/);
  const tool = toolMatch ? getToolBySlug(toolMatch[1]) : null;
  if (tool) return { title: `${tool.name} | Lucaden`, description: tool.description };
  if (path === "/pricing") {
    return {
      title: "Lucaden Pricing | Free FPS Payment Pages and HKD99/mo Pro Tools",
      description: "Create a free Hong Kong FPS payment page forever, or upgrade to HKD99/mo for branded QR, request tracking, exports, reminders, and workflow tools."
    };
  }
  if (path === "/faq" || path === "/safety") {
    return {
      title: "Lucaden FAQ | Hong Kong FPS Payment Page Builder",
      description: "Learn what Lucaden does, why it exists, how free FPS payment pages work, and how merchants can collect payment directly with zero Lucaden transaction fee."
    };
  }
  if (path === "/blog") {
    return {
      title: "Lucaden Blog | Hong Kong FPS Setup Guides for SMEs",
      description: "Long-tail Hong Kong FPS guides for shops, service businesses, invoices, QR codes, payment links, and safe payment collection."
    };
  }
  return {
    title: "Lucaden | Free Hong Kong FPS Payment Pages",
    description: "Create your own Hong Kong FPS payment page, QR code, payment request links, and payer instructions. Free forever, with optional HKD99/mo Pro tools."
  };
}

function App() {
  const [route, setRoute] = useState({ path: window.location.pathname, search: new URLSearchParams(window.location.search) });
  const [businesses, setBusinesses] = useState<BusinessProfile[]>(() => getBusinesses());
  const [requests, setRequests] = useState<PaymentRequest[]>(() => getRequests());
  const [reports, setReports] = useState<ReportRecord[]>(() => getReports());
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    const onPop = () => setRoute({ path: window.location.pathname, search: new URLSearchParams(window.location.search) });
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    reloadAuth();
  }, []);

  async function reloadAuth() {
    try {
      const data = await apiJson<{ user: AuthUser | null }>("/api/auth/me");
      setCurrentUser(data.user);
    } catch {
      setCurrentUser(null);
    } finally {
      setAuthChecked(true);
    }
  }

  function navigate(href: string) {
    window.history.pushState({}, "", href);
    setRoute({ path: window.location.pathname, search: new URLSearchParams(window.location.search) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function refresh(nextBusinesses = businesses, nextRequests = requests, nextReports = reports) {
    saveBusinesses(nextBusinesses);
    saveRequests(nextRequests);
    saveReports(nextReports);
    setBusinesses([...nextBusinesses]);
    setRequests([...nextRequests]);
    setReports([...nextReports]);
  }

  function notify(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  }

  const page = renderRoute({
    route,
    businesses,
    requests,
    reports,
    navigate,
    refresh,
    notify,
    currentUser,
    authChecked,
    reloadAuth
  });

  useEffect(() => {
    const meta = pageMeta(route.path);
    document.title = meta.title;
    let description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.appendChild(description);
    }
    description.content = meta.description;
    const setMetaProperty = (property: string, content: string) => {
      let element = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }
      element.content = content;
    };
    const normalizedPath = route.path.replace(/\/$/, "") || "/";
    const isToolPage = normalizedPath === "/tools" || Boolean(getToolBySlug(normalizedPath.replace(/^\//, "")));
    const canonicalPath = normalizedPath === "/" ? "/" : `${normalizedPath}${isToolPage ? "/" : ""}`;
    const canonicalUrl = new URL(canonicalPath, "https://lucaden.com").toString();
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
    setMetaProperty("og:title", meta.title);
    setMetaProperty("og:description", meta.description);
    setMetaProperty("og:url", canonicalUrl);
  }, [route.path]);

  return (
    <div className="app-shell">
      <Header navigate={navigate} currentUser={currentUser} reloadAuth={reloadAuth} notify={notify} />
      {page}
      <Footer navigate={navigate} />
      {toast ? <div className="toast">{toast}</div> : null}
    </div>
  );
}

type RouteContext = {
  route: { path: string; search: URLSearchParams };
  businesses: BusinessProfile[];
  requests: PaymentRequest[];
  reports: ReportRecord[];
  navigate: (href: string) => void;
  refresh: (businesses?: BusinessProfile[], requests?: PaymentRequest[], reports?: ReportRecord[]) => void;
  notify: (message: string) => void;
  currentUser: AuthUser | null;
  authChecked: boolean;
  reloadAuth: () => Promise<void>;
};

function renderRoute(ctx: RouteContext) {
  const path = ctx.route.path.replace(/\/$/, "") || "/";
  const requestMatch = path.match(/^\/pay\/([^/]+)\/request\/([^/]+)$/);
  const payMatch = path.match(/^\/pay\/([^/]+)$/);
  const reportMatch = path.match(/^\/report\/([^/]+)$/);
  const blogMatch = path.match(/^\/blog\/([^/]+)$/);

  if (requestMatch) return <PaymentPage {...ctx} slug={requestMatch[1]} requestCode={requestMatch[2]} />;
  if (payMatch) return <PaymentPage {...ctx} slug={payMatch[1]} />;
  if (reportMatch) return <ReportPage {...ctx} slug={reportMatch[1]} />;
  if (path === "/blog") return <BlogIndexPage navigate={ctx.navigate} />;
  if (blogMatch) return <BlogPostPage slug={blogMatch[1]} navigate={ctx.navigate} />;
  if (path === "/directory") return <LandingPage businesses={ctx.businesses} navigate={ctx.navigate} />;
  if (path === "/dashboard") return <DashboardPage {...ctx} />;
  if (path === "/safety") return <FaqPage navigate={ctx.navigate} />;
  if (path === "/faq") return <FaqPage navigate={ctx.navigate} />;
  if (path === "/pricing") return <PricingPage navigate={ctx.navigate} />;
  if (path === "/admin") return <AdminPage {...ctx} />;
  if (path === "/signup" || path === "/login") return <AuthPage reloadAuth={ctx.reloadAuth} notify={ctx.notify} navigate={ctx.navigate} />;
  if (path === "/tools" || getToolBySlug(path.replace(/^\//, ""))) {
    return (
      <Suspense fallback={<main className="section narrow"><div className="empty-state"><WrenchFallback /></div></main>}>
        <ToolRoute path={path} navigate={ctx.navigate} notify={ctx.notify} currentUser={ctx.currentUser} />
      </Suspense>
    );
  }
  return <LandingPage businesses={ctx.businesses} navigate={ctx.navigate} />;
}

function WrenchFallback() {
  return <><QrCode size={36} /><span>Loading tool</span></>;
}

function Header({
  navigate,
  currentUser,
  reloadAuth,
  notify
}: {
  navigate: (href: string) => void;
  currentUser: AuthUser | null;
  reloadAuth: () => Promise<void>;
  notify: (message: string) => void;
}) {
  async function logout() {
    await apiJson("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    await reloadAuth();
    notify("Logged out");
    navigate("/");
  }

  return (
    <header className="site-header">
      <button className="brand" onClick={() => navigate("/")} aria-label="Lucaden home">
        <img className="brand-logo" src="/lucaden-logo.svg" alt="" />
        <span>
          Lucaden
          <small>FPS payment profiles</small>
        </span>
      </button>
      <nav>
        {nav.map((item) => (
          <button key={item.href} onClick={() => navigate(item.href)}>
            {item.label}
          </button>
        ))}
        {currentUser ? (
          <button onClick={() => navigate("/dashboard")}>
            Dashboard
          </button>
        ) : null}
      </nav>
      {currentUser ? (
        <div className="header-account">
          <button className="icon-text primary-action" onClick={() => navigate("/dashboard")}>
            <User size={17} />
            {currentUser.name || currentUser.email}
          </button>
          <button className="icon-text" onClick={logout}>Logout</button>
        </div>
      ) : (
        <button className="icon-text primary-action" onClick={() => navigate("/dashboard")}>
          <Rocket size={17} />
          Start now
        </button>
      )}
    </header>
  );
}

function Footer({ navigate }: { navigate: (href: string) => void }) {
  return (
    <footer className="site-footer">
      <div>
        <strong>Lucaden</strong>
        <p>
          Independent FPS profile, QR, and business payment toolkit. Lucaden is not a bank,
          stored value facility, payment processor, or FPS operator. Payments go directly to the business owner.
        </p>
      </div>
      <div className="footer-links">
        <button onClick={() => navigate("/pricing")}>Pricing</button>
        <button onClick={() => navigate("/faq")}>FAQ</button>
        <button onClick={() => navigate("/blog")}>Blog</button>
        <button onClick={() => navigate("/dashboard")}>Start now</button>
      </div>
    </footer>
  );
}

function LandingPage({ businesses, navigate }: { businesses: BusinessProfile[]; navigate: (href: string) => void }) {
  return (
    <main>
      <section className="hero">
        <div className="hero-media" aria-hidden="true">
          <img src="/lucaden-counter-qr.jpg" alt="" />
          <div className="hero-overlay" />
          <div className="hero-dashboard-demo">
            <div className="demo-rail" aria-hidden="true">
              <span className="demo-brand-dot">L</span>
              <span className="demo-rail-item active"><Store size={15} /></span>
              <span className="demo-rail-item"><QrCode size={15} /></span>
              <span className="demo-rail-item"><ReceiptText size={15} /></span>
            </div>
            <div className="demo-workspace">
              <div className="demo-topline">
                <div>
                  <span>ABC Florist</span>
                  <strong>Payment dashboard</strong>
                </div>
                <BadgeCheck size={17} />
              </div>
              <div className="demo-panel-row">
                <div className="demo-qr-card">
                  <div className="mini-qr demo-qr"><QrCode size={78} /></div>
                  <small><span className="demo-pulse-dot" /> Static profile QR</small>
                </div>
                <div className="demo-form-card">
                  <span>FPS proxy</span>
                  <strong>1234567</strong>
                  <span>Invoice</span>
                  <strong>HK$500</strong>
                  <small>Dynamic link updates</small>
                </div>
              </div>
              <div className="demo-request-card">
                <div>
                  <strong>INV1001 deposit</strong>
                  <span>Ref INV1001</span>
                </div>
                <small>Marked received</small>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-copy">
          <p className="eyebrow">Hong Kong FPS business toolkit</p>
          <h1>Create your payment page for FPS in minutes.</h1>
          <p>
            Lucaden exists for people and teams without spare IT resources. Build a dedicated store-style URL,
            generate a static profile QR or dynamic amount/reference QR links, and share invoice-ready instructions.
            Customers pay you directly. Lucaden charges 0%.
          </p>
          <div className="hero-actions">
            <button className="button primary" onClick={() => navigate("/dashboard")}>
              <Rocket size={18} />
              Start now
            </button>
            <button className="button secondary" onClick={() => navigate("/pay/abc-florist?amount=500&ref=INV1001&purpose=Deposit")}>
              <QrCode size={18} />
              View sample payment page
            </button>
          </div>
        </div>
      </section>

      <section className="trust-strip">
        <span><ShieldCheck size={18} /> No fund custody</span>
        <span><CircleDollarSign size={18} /> 0% transaction fee</span>
        <span><Lock size={18} /> Free features stay free forever</span>
        <span><Languages size={18} /> Static and dynamic QR links</span>
      </section>

      <section className="section qr-explainer-band">
        <div className="section-heading">
          <p className="eyebrow">Why Lucaden exists</p>
          <h2>Static and dynamic QR setup without an IT project.</h2>
          <p>
            Our purpose is to remove the usual setup cost, vendor delay, and developer work around simple FPS
            collection. Merchants get a payment page, QR downloads, and invoice-ready links from the browser.
          </p>
        </div>
        <div className="qr-explainer-grid">
          <article className="qr-explainer-card">
            <QrCode size={24} />
            <span>Static QR</span>
            <h3>One QR for your payment profile.</h3>
            <p>
              Use it on counters, invoices, receipts, and social profiles. It opens your public Lucaden page with
              your current FPS receiving details, recipient name, and payer instructions.
            </p>
          </article>
          <article className="qr-explainer-card">
            <ReceiptText size={24} />
            <span>Dynamic QR</span>
            <h3>One QR or link for a specific payment.</h3>
            <p>
              Create a link for an invoice, order, deposit, bundle, customer, amount, expiry, or reference. The
              customer sees the context before paying you directly in their own bank or wallet app.
            </p>
          </article>
          <article className="qr-explainer-card">
            <CircleDollarSign size={24} />
            <span>Cost removal</span>
            <h3>Useful before a custom integration makes sense.</h3>
            <p>
              Lucaden keeps the core QR and payment-link workflow free, so small teams can avoid paying IT just to
              publish payment instructions and track invoice records.
            </p>
          </article>
        </div>
      </section>

      <section className="section grid-two">
        <div>
          <p className="eyebrow">Direct-to-owner FPS collection</p>
          <h2>Eliminate the setup cost around everyday FPS QR workflows.</h2>
          <p>
            Many Hong Kong businesses want to receive FPS, but IT setup, vendor quotes, and merchant banking
            workflows can slow everything down. Lucaden turns your FPS receiving details into a clean page you can
            share on WhatsApp, invoices, Instagram, or your website, with static profile QR downloads and dynamic
            payment links for specific orders, deposits, products, or references.
          </p>
        </div>
        <div className="feature-grid">
          <Feature icon={<Store />} title="Dedicated payment page" text="A business profile URL with FPS details, amount/reference display, copy buttons, and payer instructions." />
          <Feature icon={<QrCode />} title="URL QR generation" text="QR codes point to payment pages first, avoiding unverified FPS payload encoding risk." />
          <Feature icon={<Send />} title="Payment request links" text="Create amount, purpose, and reference-specific links for WhatsApp, invoices, or DMs." />
          <Feature icon={<WalletCards />} title="No middleman fees" text="Your customer pays directly to your bank or wallet account. Lucaden does not take a transaction cut." />
        </div>
      </section>

      <section className="section workflow">
        <div className="section-heading">
          <p className="eyebrow">Free forever core tools</p>
          <h2>Start collecting with the basics at HKD0.</h2>
        </div>
        {[
          "Create and publish your own business payment profile",
          "Input FPS receiving details with masking controls",
          "Generate public payment page QR codes and PNG downloads",
          "Create payment request links with amount and reference",
          "Share one profile URL as your payment site profile",
          "Add clear payer instructions for invoice, order, or deposit references",
          "Keep compliance copy clear: no payment processing claims"
        ].map((item) => (
          <div className="workflow-step" key={item}>
            <Check size={18} />
            <span>{item}</span>
          </div>
        ))}
      </section>

      <MerchantStories />
    </main>
  );
}

function Feature({ icon, title, text }: { icon: JSX.Element; title: string; text: string }) {
  return (
    <article className="feature">
      {icon}
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

function BusinessCard({ business, navigate }: { business: BusinessProfile; navigate: (href: string) => void }) {
  return (
    <article className="business-card">
      <div className="business-card-head">
        <span className="logo-dot">{business.businessName.slice(0, 1)}</span>
        <div>
          <h3>{business.businessName}</h3>
          <p>{business.category} - {business.district}</p>
        </div>
      </div>
      <p>{business.shortDescription}</p>
      <div className="badge-row">
        <span className={business.verificationLevel > 0 ? "badge verified" : "badge"}>{verificationLabel(business.verificationLevel)}</span>
        <span className="badge green">Accepts FPS</span>
      </div>
      <button className="button secondary full" onClick={() => navigate(`/pay/${business.slug}`)}>
        <ExternalLink size={17} />
        View profile
      </button>
    </article>
  );
}

function MerchantStories() {
  const [visibleCount, setVisibleCount] = useState(15);
  const visibleStories = merchantStories.slice(0, visibleCount);
  const remaining = merchantStories.length - visibleStories.length;

  return (
    <section className="section merchant-stories">
      <div className="section-heading">
        <p className="eyebrow">Merchant stories</p>
        <h2>Payment workflows that feel clear, direct, and easier to repeat.</h2>
        <p>
          Real payment operations usually come down to clean instructions, correct references, and fewer repeated
          questions. These examples show the kinds of workflows Lucaden is built to support.
        </p>
      </div>
      <div className="story-grid">
        {visibleStories.map((story) => (
          <article className="story-card" key={`${story.name}-${story.role}`}>
            <div className="story-stars" aria-label="Positive workflow note">
              {[0, 1, 2, 3, 4].map((item) => <Star key={item} size={15} fill="currentColor" />)}
            </div>
            <p>{story.quote}</p>
            <div className="story-person">
              {story.avatar ? <img src={story.avatar} alt="" loading="lazy" /> : <span>{story.name.slice(0, 1)}</span>}
              <div>
                <strong>{story.name}</strong>
                <small>{story.role}</small>
              </div>
            </div>
          </article>
        ))}
      </div>
      {remaining > 0 ? (
        <button className="button secondary story-more" onClick={() => setVisibleCount((count) => Math.min(count + 15, merchantStories.length))}>
          <Users size={17} />
          Show 15 more
        </button>
      ) : null}
    </section>
  );
}

function PaymentPage({ route, businesses, requests, slug, requestCode, navigate, notify, currentUser }: RouteContext & { slug: string; requestCode?: string }) {
  const [remoteBusiness, setRemoteBusiness] = useState<BusinessProfile | null>(null);
  const [remoteRequest, setRemoteRequest] = useState<PaymentRequest | null>(null);
  const [remoteLoaded, setRemoteLoaded] = useState(false);
  const [canEditRemote, setCanEditRemote] = useState(false);
  useEffect(() => {
    setRemoteLoaded(false);
    setCanEditRemote(false);
    apiJson<{ business: BusinessProfile | null; request: PaymentRequest | null; canEdit?: boolean }>(
      `/api/public/pay?slug=${encodeURIComponent(slug)}${requestCode ? `&requestCode=${encodeURIComponent(requestCode)}` : ""}`
    )
      .then((data) => {
        setRemoteBusiness(data.business);
        setRemoteRequest(data.request);
        setCanEditRemote(Boolean(data.canEdit));
      })
      .catch(() => undefined)
      .finally(() => setRemoteLoaded(true));
  }, [slug, requestCode]);
  const fallbackBusiness = businesses.find((item) => item.slug === slug && item.status !== "suspended");
  const business = remoteBusiness || fallbackBusiness;
  const request = remoteRequest || (requestCode ? requests.find((item) => item.requestCode.toLowerCase() === requestCode.toLowerCase() && item.businessId === business?.id) : undefined);
  const queryAmount = route.search.get("amount") || "";
  const queryRef = route.search.get("ref") || "";
  const queryPurpose = route.search.get("purpose") || "";
  const amount = request?.amount || queryAmount;
  const reference = request?.reference || queryRef;
  const purpose = request?.title || queryPurpose || business?.defaultPaymentNote || "";
  const pageUrl = request
    ? `${siteOrigin()}/pay/${slug}/request/${request.requestCode}`
    : `${siteOrigin()}/pay/${slug}${route.search.toString() ? `?${route.search.toString()}` : ""}`;
  const [qr, setQr] = useState("");

  useEffect(() => {
    toQrDataUrl(pageUrl).then(setQr);
  }, [pageUrl]);

  if (!business && !remoteLoaded) {
    return (
      <main className="section narrow">
        <div className="empty-state">
          <QrCode size={42} />
          <h1>Loading payment profile</h1>
        </div>
      </main>
    );
  }

  if (!business) {
    return (
      <main className="section narrow">
        <div className="empty-state">
          <Ban size={42} />
          <h1>Payment profile unavailable</h1>
          <p>This profile may not exist, may still be in draft, or may have been suspended for review.</p>
          <button className="button primary" onClick={() => navigate("/dashboard")}><Rocket size={17} /> Create your payment page</button>
        </div>
      </main>
    );
  }

  const visibleProxy = business.hideRawProxy ? maskFps(business.fpsProxyValue) : business.fpsProxyValue;
  const canEdit = canEditRemote || Boolean(currentUser && business.ownerEmail && business.ownerEmail === currentUser.email);

  return (
    <main className="payment-page">
      <section className="payment-header">
        <div className="merchant-identity">
          <span className="logo-dot large">{business.businessName.slice(0, 1)}</span>
          <div>
            <span className={business.verificationLevel > 0 ? "badge verified" : "badge"}>{verificationLabel(business.verificationLevel)}</span>
            <h1>{business.businessName}</h1>
            <p>{business.category} - {business.district}</p>
          </div>
        </div>
        <div className="payment-header-actions">
          {canEdit ? (
            <button className="button primary" onClick={() => navigate("/dashboard")}>
              <Settings size={17} />
              Edit details
            </button>
          ) : null}
          <button className="button secondary" onClick={() => navigate(`/report/${business.slug}`)}>
            <Flag size={17} />
            Report profile
          </button>
        </div>
      </section>

      <section className="payment-layout">
        <div className="pay-panel">
          {(request?.productName || request?.bundleName || request?.discountOffer) ? (
            <div className="offer-box">
              {request.productName ? <strong>{request.productName}</strong> : null}
              {request.bundleName ? <span>{request.bundleName}</span> : null}
              {request.discountOffer ? <small>{request.discountOffer}</small> : null}
              {request.offerDetails ? <p>{request.offerDetails}</p> : null}
            </div>
          ) : null}
          {amount ? (
            <div className="amount-box">
              <span>Amount</span>
              <strong>{money(amount, request?.currency || business.currency)}</strong>
              {purpose ? <p>{purpose}</p> : null}
            </div>
          ) : (
            <div className="amount-box subtle">
              <span>Payment page</span>
              <strong>Open amount</strong>
              <p>Enter the correct amount in your own bank or wallet app.</p>
            </div>
          )}
          <div className="qr-frame">{qr ? <img src={qr} alt={`QR code for ${business.businessName} payment page`} /> : <QrCode size={128} />}</div>
          <div className="action-row">
            <button className="button secondary" onClick={() => downloadBrandedQrPng(pageUrl, `${business.slug}-lucaden-qr.png`, business.logoDataUrl || "", business.businessName)}>
              <Download size={17} />
              Download QR
            </button>
            <button className="button secondary" onClick={() => copyText(pageUrl).then(() => notify("Payment link copied"))}>
              <LinkIcon size={17} />
              Copy link
            </button>
          </div>
          <p className="fine-print">QR opens this Lucaden payment page. It is not a raw FPS payload QR.</p>
        </div>

        <div className="details-panel">
          <h2>FPS receiving details</h2>
          <DetailRow label="Proxy type" value={business.fpsProxyType} />
          <DetailRow label="FPS receiving detail" value={visibleProxy} />
          <DetailRow label="Expected recipient" value={business.expectedRecipientName || "Check payer app before confirming"} />
          <DetailRow label="Currency" value={request?.currency || business.currency} />
          {reference ? <DetailRow label="Reference" value={reference} /> : null}
          {business.bankName ? <DetailRow label="Bank / SVF note" value={business.bankName} /> : null}
          <div className="copy-grid">
            {business.showCopyButton && !business.hideRawProxy ? (
              <button className="button primary" onClick={() => copyText(business.fpsProxyValue).then(() => notify("FPS detail copied"))}>
                <ClipboardCopy size={17} />
                Copy FPS detail
              </button>
            ) : null}
            {reference ? (
              <button className="button secondary" onClick={() => copyText(reference).then(() => notify("Reference copied"))}>
                <ClipboardCopy size={17} />
                Copy reference
              </button>
            ) : null}
            {business.whatsapp ? (
              <a className="button whatsapp" href={`https://wa.me/${business.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
                <MessageCircle size={17} />
                WhatsApp
              </a>
            ) : null}
          </div>
          <div className="instruction-box">
            <h3>Payment instructions</h3>
            <p>{business.paymentRemarksInstruction || "Use the shown reference or business name in your FPS remarks field."}</p>
            <p className="zh">Before paying, check the recipient name, amount, and payment details in your own bank or wallet app.</p>
          </div>
          <SafetyNotice />
        </div>
      </section>
    </main>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="detail-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SafetyNotice() {
  return (
    <div className="safety-notice">
      <AlertTriangle size={19} />
      <div>
        <strong>Before paying</strong>
        <p>
          FPS payments are instant. Please verify the recipient name, amount, and payment details in your bank or
          wallet app before confirming. Lucaden does not hold, process, settle, refund, or confirm funds.
        </p>
      </div>
    </div>
  );
}

function DirectoryPage({ businesses, navigate }: RouteContext) {
  const [liveBusinesses, setLiveBusinesses] = useState<BusinessProfile[]>(businesses);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [district, setDistrict] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  useEffect(() => {
    apiJson<{ businesses: BusinessProfile[] }>("/api/public/directory")
      .then((data) => {
        const merged = [...data.businesses, ...businesses.filter((seed) => !data.businesses.some((live) => live.slug === seed.slug))];
        setLiveBusinesses(merged);
      })
      .catch(() => setLiveBusinesses(businesses));
  }, [businesses]);
  const filtered = liveBusinesses.filter((business) => {
    if (!business.directoryVisible || business.status !== "published") return false;
    if (category && business.category !== category) return false;
    if (district && business.district !== district) return false;
    if (verifiedOnly && business.verificationLevel === 0) return false;
    const haystack = `${business.businessName} ${business.shortDescription} ${business.category} ${business.district}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  return (
    <main className="section">
      <div className="page-title">
        <p className="eyebrow">FPS-ready merchants</p>
        <h1>Hong Kong business directory</h1>
        <p>Directory listings are user-submitted unless marked verified. Always verify payee details before payment.</p>
      </div>
      <div className="filter-bar">
        <label className="search-input">
          <Search size={18} />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search business, category, district" />
        </label>
        <select value={category} onChange={(event) => setCategory(event.target.value)}>
          <option value="">All categories</option>
          {categories.map((item) => <option key={item}>{item}</option>)}
        </select>
        <select value={district} onChange={(event) => setDistrict(event.target.value)}>
          <option value="">All districts</option>
          {districts.map((item) => <option key={item}>{item}</option>)}
        </select>
        <label className="toggle">
          <input type="checkbox" checked={verifiedOnly} onChange={(event) => setVerifiedOnly(event.target.checked)} />
          Verified only
        </label>
      </div>
      <div className="business-row">
        {filtered.map((business) => <BusinessCard key={business.id} business={business} navigate={navigate} />)}
      </div>
    </main>
  );
}

function stableFingerprint(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(36).padStart(6, "0").slice(0, 8);
}

function requestDraftFromForm(form: FormData, fallbackCurrency: Currency): PaymentRequestDraft {
  return {
    amount: String(form.get("amount") || ""),
    currency: String(form.get("currency") || fallbackCurrency),
    title: String(form.get("title") || ""),
    productName: String(form.get("productName") || ""),
    bundleName: String(form.get("bundleName") || ""),
    discountOffer: String(form.get("discountOffer") || ""),
    offerDetails: String(form.get("offerDetails") || ""),
    reference: String(form.get("reference") || ""),
    customerName: String(form.get("customerName") || ""),
    customerContact: String(form.get("customerContact") || ""),
    expiresAt: String(form.get("expiresAt") || ""),
    internalNote: String(form.get("internalNote") || "")
  };
}

function DashboardPage({ currentUser, authChecked, reloadAuth, notify, navigate }: RouteContext) {
  const [business, setBusiness] = useState<BusinessProfile | null>(null);
  const [profileDraft, setProfileDraft] = useState<BusinessProfile | null>(() => readGuestDraft());
  const [businessRequests, setBusinessRequests] = useState<PaymentRequest[]>([]);
  const [erp, setErp] = useState<ErpIntegration | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [profileQr, setProfileQr] = useState("");
  const [loading, setLoading] = useState(false);
  const [authPrompt, setAuthPrompt] = useState<AuthIntent | null>(() => readAuthIntent());
  const [pendingRequestDraft, setPendingRequestDraft] = useState<PaymentRequestDraft | null>(() => readPendingRequestDraft());
  const fpsInputRef = useRef<HTMLInputElement | null>(null);
  const authPromptRef = useRef<HTMLElement | null>(null);
  const profileSectionRef = useRef<HTMLElement | null>(null);
  const paymentsSectionRef = useRef<HTMLElement | null>(null);
  const viewer: AuthUser = currentUser || {
    id: "guest",
    email: "",
    name: "",
    provider: "guest",
    plan: "free"
  };
  const isGuest = !currentUser;

  function defaultBusinessFor(user: AuthUser, logo = ""): BusinessProfile {
    return {
      id: "",
      businessName: user.name || "My business",
      slug: slugify(user.name || user.email.split("@")[0] || "my-business"),
      category: "Other",
      district: "Central and Western",
      shortDescription: "FPS-ready business profile.",
      longDescription: "",
      contactPerson: user.name || "",
      whatsapp: "",
      phone: "",
      email: user.email,
      website: "",
      instagram: "",
      address: "",
      logoDataUrl: logo,
      directoryVisible: false,
      status: user.id === "guest" ? "draft" : "published",
      verificationLevel: user.id === "guest" ? 0 : 1,
      fpsProxyType: "FPS ID",
      fpsProxyValue: "",
      currency: "HKD",
      expectedRecipientName: user.name || "",
      bankName: "",
      paymentRemarksInstruction: "Use invoice, order, product, or bundle reference in FPS remarks.",
      defaultPaymentNote: "",
      hideRawProxy: false,
      showCopyButton: true,
      updatedAt: new Date().toISOString()
    };
  }

  const active: BusinessProfile = profileDraft || business || defaultBusinessFor(viewer, logoDataUrl);
  const publicUrl = `${siteOrigin()}/pay/${active.slug}`;
  const liveQrSignature = stableFingerprint(`${active.fpsProxyType}|${active.fpsProxyValue}|${active.expectedRecipientName}`);
  const liveQrUrl = active.fpsProxyValue.trim()
    ? `${publicUrl}?profile=${liveQrSignature}`
    : publicUrl;
  const isPro = currentUser?.plan === "pro";

  useEffect(() => {
    if (currentUser) {
      loadDashboard();
    } else {
      setBusiness(null);
      setBusinessRequests([]);
      setErp(null);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (!authChecked) return;
    if (!currentUser) {
      const draft = profileDraft || readGuestDraft() || defaultBusinessFor(viewer, logoDataUrl);
      setProfileDraft(draft);
      setLogoDataUrl(draft.logoDataUrl || "");
      return;
    }
    const guestDraft = readAuthIntent() ? readGuestDraft() : null;
    const nextProfile = guestDraft
      ? {
          ...guestDraft,
          ownerEmail: currentUser.email,
          email: guestDraft.email || currentUser.email,
          contactPerson: guestDraft.contactPerson || currentUser.name,
          status: "published" as const,
          verificationLevel: 1 as const
        }
      : business || defaultBusinessFor(currentUser, logoDataUrl);
    setProfileDraft(nextProfile);
    setLogoDataUrl(nextProfile.logoDataUrl || "");
  }, [authChecked, currentUser?.id, business?.id, business?.updatedAt]);

  useEffect(() => {
    if (authPrompt) {
      authPromptRef.current?.focus();
    }
  }, [authPrompt]);

  useEffect(() => {
    let cancelled = false;
    toQrDataUrl(liveQrUrl).then((dataUrl) => {
      if (!cancelled) setProfileQr(dataUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [liveQrUrl]);

  useEffect(() => {
    if (!currentUser || !authPrompt) return;
    let cancelled = false;
    const pending = pendingRequestDraft || readPendingRequestDraft();

    async function finishAccountAction() {
      try {
        const saved = await saveProfileDraft(false);
        if (!saved || cancelled) return;
        if (authPrompt === "request" && pending) {
          const data = await postPaymentRequest(pending);
          if (cancelled) return;
          setBusinessRequests((items) => [data.request, ...items]);
          clearLocalValue(PENDING_REQUEST_KEY);
          setPendingRequestDraft(null);
          notify("Account created and payment link generated");
        } else {
          notify("Account created and payment page saved");
        }
        clearLocalValue(AUTH_INTENT_KEY);
        setAuthPrompt(null);
      } catch (error) {
        if (!cancelled) notify(error instanceof Error ? error.message : "Could not finish setup");
      }
    }

    finishAccountAction();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, authPrompt]);

  async function loadDashboard() {
    setLoading(true);
    try {
      const businessData = await apiJson<{ user: AuthUser; business: BusinessProfile | null; erp: ErpIntegration | null }>("/api/business");
      const requestsData = await apiJson<{ requests: PaymentRequest[] }>("/api/payment-requests");
      setBusiness(businessData.business);
      setLogoDataUrl(businessData.business?.logoDataUrl || "");
      setErp(businessData.erp);
      setBusinessRequests(requestsData.requests);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }

  if (!authChecked) {
    return (
      <main className="section narrow">
        <div className="empty-state"><User size={42} /><h1>Checking session</h1></div>
      </main>
    );
  }

  function updateProfile(patch: Partial<BusinessProfile>) {
    setProfileDraft((current) => {
      const next = { ...(current || active), ...patch, updatedAt: new Date().toISOString() };
      if (isGuest) saveGuestDraft(next);
      return next;
    });
  }

  function updateBusinessName(value: string) {
    setProfileDraft((current) => {
      const draft = current || active;
      const shouldSyncSlug = !draft.slug || draft.slug === slugify(draft.businessName);
      const next = {
        ...draft,
        businessName: value,
        slug: shouldSyncSlug ? slugify(value) : draft.slug,
        expectedRecipientName: draft.expectedRecipientName || value,
        updatedAt: new Date().toISOString()
      };
      if (isGuest) saveGuestDraft(next);
      return next;
    });
  }

  function focusFpsDetails() {
    fpsInputRef.current?.focus();
    fpsInputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  function requestAuth(intent: AuthIntent, message: string) {
    saveGuestDraft(profileDraft || active);
    saveAuthIntent(intent);
    setAuthPrompt(intent);
    notify(message);
  }

  function dismissAuthPrompt() {
    clearLocalValue(AUTH_INTENT_KEY);
    setAuthPrompt(null);
  }

  function scrollToDashboardSection(ref: RefObject<HTMLElement>) {
    ref.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  async function saveProfileDraft(showMessage = true) {
    const profile = profileDraft || active;
    if (!currentUser) {
      requestAuth("profile", "Create a free account to publish and save your payment page.");
      return null;
    }
    const data = await apiJson<{ business: BusinessProfile }>("/api/business", {
      method: "POST",
      body: JSON.stringify({
        businessName: profile.businessName,
        slug: profile.slug,
        category: profile.category,
        district: profile.district,
        shortDescription: profile.shortDescription,
        longDescription: profile.longDescription,
        contactPerson: profile.contactPerson,
        whatsapp: profile.whatsapp,
        phone: profile.phone,
        email: profile.email,
        website: profile.website,
        instagram: profile.instagram,
        address: profile.address,
        logoDataUrl: profile.logoDataUrl || logoDataUrl,
        fpsProxyType: profile.fpsProxyType,
        fpsProxyValue: profile.fpsProxyValue,
        currency: profile.currency,
        expectedRecipientName: profile.expectedRecipientName,
        bankName: profile.bankName || "",
        paymentRemarksInstruction: profile.paymentRemarksInstruction,
        defaultPaymentNote: profile.defaultPaymentNote,
        hideRawProxy: profile.hideRawProxy,
        showCopyButton: profile.showCopyButton,
        directoryVisible: profile.directoryVisible
      })
    });
    setBusiness(data.business);
    setProfileDraft(data.business);
    setLogoDataUrl(data.business.logoDataUrl || "");
    clearLocalValue(GUEST_DRAFT_KEY);
    clearLocalValue(AUTH_INTENT_KEY);
    if (showMessage) notify("Business profile saved");
    return data.business;
  }

  async function createProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await saveProfileDraft(true);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not save profile");
    }
  }

  async function createRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const requestDraft = requestDraftFromForm(form, active.currency);
    if (!currentUser) {
      savePendingRequestDraft(requestDraft);
      setPendingRequestDraft(requestDraft);
      requestAuth("request", "Create a free account to generate and save this payment link.");
      return;
    }
    try {
      await saveProfileDraft(false);
      const data = await postPaymentRequest(requestDraft);
      setBusinessRequests((items) => [data.request, ...items]);
      clearLocalValue(PENDING_REQUEST_KEY);
      setPendingRequestDraft(null);
      notify("Payment request created");
      formElement.reset();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not create request");
    }
  }

  async function postPaymentRequest(requestDraft: PaymentRequestDraft) {
    return apiJson<{ request: PaymentRequest }>("/api/payment-requests", {
      method: "POST",
      body: JSON.stringify(requestDraft)
    });
  }

  async function markPaid(requestId: string) {
    const current = businessRequests.find((item) => item.id === requestId);
    if (current?.status === "Merchant marked as paid") return;
    try {
      await apiJson("/api/payment-requests", {
        method: "PATCH",
        body: JSON.stringify({ id: requestId, status: "Merchant marked as paid" })
      });
      setBusinessRequests((items) => items.map((item) => item.id === requestId ? { ...item, status: "Merchant marked as paid" as RequestStatus } : item));
      notify("Request marked as paid");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not mark request as paid");
    }
  }

  async function saveErp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isPro) {
      notify("ERP webhooks are available on Pro.");
      navigate("/pricing");
      return;
    }
    const form = new FormData(event.currentTarget);
    try {
      await apiJson("/api/erp", {
        method: "POST",
        body: JSON.stringify({
          provider: String(form.get("provider") || "Generic webhook"),
          webhookUrl: String(form.get("webhookUrl") || ""),
          sharedSecret: String(form.get("sharedSecret") || ""),
          enabled: form.get("enabled") === "on"
        })
      });
      const data = await apiJson<{ erp: ErpIntegration | null }>("/api/erp");
      setErp(data.erp);
      notify("ERP webhook saved");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not save ERP settings");
    }
  }

  async function testErp() {
    if (!isPro) {
      notify("ERP webhooks are available on Pro.");
      navigate("/pricing");
      return;
    }
    try {
      const result = await apiJson<{ status: number; ok: boolean }>("/api/erp/test", { method: "POST" });
      notify(`ERP ping returned HTTP ${result.status}`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "ERP ping failed");
    }
  }

  function openProFeature(feature: string) {
    notify(`${feature} is included in Pro.`);
    navigate("/pricing");
  }

  const lockedFeatures = [
    ["Logo QR studio", "Brand every payment QR"],
    ["ERP sync", "Bridge data to SAP, Oracle, NetSuite, Xero"],
    ["Bulk links", "Create many payment requests at once"],
    ["Team access", "Let staff create links safely"]
  ];
  const paidRequests = businessRequests.filter((request) => request.status === "Merchant marked as paid");
  const openRequests = businessRequests.filter((request) => request.status !== "Merchant marked as paid" && request.status !== "Cancelled" && request.status !== "Expired");
  const receivedTotal = paidRequests.reduce((sum, request) => {
    const amount = Number(request.amount || 0);
    return request.currency === active.currency && Number.isFinite(amount) ? sum + amount : sum;
  }, 0);
  const receivedDisplay = receivedTotal > 0 ? money(receivedTotal, active.currency) : `${active.currency} 0`;

  return (
    <main className="dashboard">
      <aside className="sidebar">
        <h1>{isGuest ? "Build your page" : "Dashboard"}</h1>
        <button className="active" onClick={() => scrollToDashboardSection(profileSectionRef)}><Store size={17} /> Profile setup</button>
        <button
          onClick={async () => {
            try {
              const saved = await saveProfileDraft(false);
              if (saved) navigate(`/pay/${saved.slug}`);
            } catch (error) {
              notify(error instanceof Error ? error.message : "Save the profile first");
            }
          }}
        >
          <ExternalLink size={17} /> Public page
        </button>
        <button onClick={() => scrollToDashboardSection(paymentsSectionRef)}>
          <ReceiptText size={17} /> Payments / invoices
        </button>
        {!isPro ? (
          <div className="sidebar-pro">
            <span className="sidebar-label">Locked Pro tools</span>
            {lockedFeatures.map(([title, text]) => (
              <button className="locked-tool" key={title} onClick={() => openProFeature(title)}>
                <Lock size={16} />
                <span>
                  {title}
                  <small>{text}</small>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="sidebar-pro">
            <span className="sidebar-label">Pro active</span>
            <button className="locked-tool unlocked" onClick={() => navigate("/pricing")}>
              <Crown size={16} />
              <span>
                HKD99/mo
                <small>Business tools unlocked</small>
              </span>
            </button>
          </div>
        )}
      </aside>

      <div className="dashboard-content">
        <section className="overview-grid">
          <Stat icon={<Globe2 />} label="Profile status" value={isGuest ? "draft preview" : business ? active.status : "setup needed"} />
          <Stat icon={<QrCode />} label="QR" value={isPro ? "Logo QR enabled" : "Free URL QR"} />
          <Stat icon={<ReceiptText />} label="Payment records" value={String(businessRequests.length)} />
          <Stat icon={<Crown />} label="Plan" value={isPro ? "Pro HKD99/mo" : "Free forever"} />
        </section>

        <section className="dashboard-section qr-focus-panel">
          <div>
            <p className="eyebrow">QR center</p>
            <h2>Static profile QR ready to share.</h2>
            <p>
              This QR opens your public payment profile. Use dynamic payment links below when you need amount,
              product, offer, reference, customer, or expiry details baked into the URL.
            </p>
            <div className="qr-mode-list">
              <span><QrCode size={16} /> Static profile QR</span>
              <span><ReceiptText size={16} /> Dynamic request QR links</span>
              <span><Sparkles size={16} /> Live FPS preview</span>
            </div>
          </div>
          <div className="dashboard-qr-card">
            <div className="dashboard-qr-frame live-qr-frame" key={liveQrSignature}>
              {profileQr ? <img src={profileQr} alt={`QR code for ${active.businessName} payment page`} /> : <QrCode size={96} />}
            </div>
            <small className="qr-live-note">
              {active.fpsProxyValue ? `${active.fpsProxyType} preview: ${active.hideRawProxy ? maskFps(active.fpsProxyValue) : active.fpsProxyValue}` : "Add FPS proxy details to see the QR preview react."}
            </small>
            <button
              className="button primary full"
              onClick={() => isGuest
                ? requestAuth("profile", "Create a free account to publish and download your payment page QR.")
                : downloadQrPng(liveQrUrl, `${active.slug}-payment-page-qr.png`)}
            >
              <Download size={17} />
              Download QR
            </button>
            <button
              className="button secondary full"
              onClick={() => isGuest
                ? requestAuth("profile", "Create a free account to publish and copy your payment page link.")
                : copyText(publicUrl).then(() => notify("Public link copied"))}
            >
              <ClipboardCopy size={17} />
              Copy link
            </button>
          </div>
        </section>

        {authPrompt ? (
          <div
            className="auth-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) dismissAuthPrompt();
            }}
          >
            <section
              className="auth-modal"
              role="dialog"
              aria-modal="true"
              aria-label={authPrompt === "request" ? "Create account to generate payment link" : "Create account to publish payment page"}
              ref={authPromptRef}
              tabIndex={-1}
            >
              <button className="icon-button modal-close" type="button" aria-label="Close account prompt" onClick={dismissAuthPrompt}>
                <X size={18} />
              </button>
              <AuthPanel
                reloadAuth={reloadAuth}
                notify={notify}
                navigate={navigate}
                title={authPrompt === "request" ? "Create your account to generate this payment link" : "Create your account to publish this payment page"}
                text="Your draft details are already saved in this browser. Continue with Google or create a free account with email."
                compact
              />
            </section>
          </div>
        ) : null}

        <section className="dashboard-section split" ref={profileSectionRef}>
          <form onSubmit={createProfile} className="form-panel">
            <h2>Your payment profile</h2>
            <div className="form-grid">
              <Field label="Business name" name="businessName" value={active.businessName} onChange={updateBusinessName} />
              <Field label="Public URL slug" name="slug" value={active.slug} onChange={(value) => updateProfile({ slug: slugify(value) })} />
              <SelectField label="Category" name="category" options={categories} value={active.category} onChange={(value) => updateProfile({ category: value })} />
              <SelectField label="District" name="district" options={districts} value={active.district} onChange={(value) => updateProfile({ district: value })} />
              <Field label="Short description" name="shortDescription" value={active.shortDescription} onChange={(value) => updateProfile({ shortDescription: value })} />
              <Field label="Contact person" name="contactPerson" value={active.contactPerson} onChange={(value) => updateProfile({ contactPerson: value })} />
              <Field label="WhatsApp" name="whatsapp" value={active.whatsapp} onChange={(value) => updateProfile({ whatsapp: value })} />
              <Field label="Email" name="email" value={active.email} onChange={(value) => updateProfile({ email: value })} type="email" />
              <SelectField label="FPS proxy type" name="fpsProxyType" options={["FPS ID", "Mobile", "Email"]} value={active.fpsProxyType} onChange={(value) => updateProfile({ fpsProxyType: value as ProxyType })} />
              <Field label="FPS proxy value" name="fpsProxyValue" value={active.fpsProxyValue} onChange={(value) => updateProfile({ fpsProxyValue: value })} inputRef={fpsInputRef} />
              <SelectField label="Currency" name="currency" options={["HKD", "RMB"]} value={active.currency} onChange={(value) => updateProfile({ currency: value as Currency })} />
              <Field label="Expected recipient name" name="expectedRecipientName" value={active.expectedRecipientName} onChange={(value) => updateProfile({ expectedRecipientName: value })} />
              <Field label="Bank / SVF note" name="bankName" value={active.bankName || ""} onChange={(value) => updateProfile({ bankName: value })} />
              <Field label="Payment remarks instruction" name="paymentRemarksInstruction" value={active.paymentRemarksInstruction} onChange={(value) => updateProfile({ paymentRemarksInstruction: value })} />
              <Field label="Default payment note" name="defaultPaymentNote" value={active.defaultPaymentNote} onChange={(value) => updateProfile({ defaultPaymentNote: value })} />
              <label>
                <span>Business logo for QR</span>
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  if (file) {
                    fileToDataUrl(file).then((dataUrl) => {
                      setLogoDataUrl(dataUrl);
                      updateProfile({ logoDataUrl: dataUrl });
                    });
                  }
                }} />
              </label>
              <div className="logo-preview">{logoDataUrl ? <img src={logoDataUrl} alt="" /> : <span>No logo</span>}</div>
            </div>
            <div className="switch-row">
              <label><input type="checkbox" name="hideRawProxy" checked={active.hideRawProxy} onChange={(event) => updateProfile({ hideRawProxy: event.currentTarget.checked })} /> Hide raw FPS value</label>
              <label><input type="checkbox" name="showCopyButton" checked={active.showCopyButton} onChange={(event) => updateProfile({ showCopyButton: event.currentTarget.checked })} /> Show copy button</label>
            </div>
            <button className="button primary" type="submit" disabled={loading}>
              <UserPlus size={17} />
              {isGuest ? "Create account to publish" : "Save profile"}
            </button>
          </form>

          <div className="form-panel">
            <h2>{isGuest ? "Your draft payment URL" : "Your live payment URL"}</h2>
            <div className="detail-row"><span>Payment page</span><strong>{publicUrl}</strong></div>
            <div className="detail-row">
              <span>{active.fpsProxyType}</span>
              {active.fpsProxyValue ? (
                <strong>{active.hideRawProxy ? maskFps(active.fpsProxyValue) : active.fpsProxyValue}</strong>
              ) : (
                <button className="inline-link" type="button" onClick={focusFpsDetails}>Add FPS details</button>
              )}
            </div>
            <div className="detail-row"><span>Expected recipient</span><strong>{active.expectedRecipientName || "Add recipient name"}</strong></div>
            <div className="action-row">
              <button
                className="button secondary"
                onClick={() => isGuest
                  ? requestAuth("profile", "Create a free account to publish and copy your payment page link.")
                  : copyText(publicUrl).then(() => notify("Public link copied"))}
              >
                <ClipboardCopy size={17} />
                Copy link
              </button>
              <button
                className="button secondary"
                onClick={() => isGuest
                  ? requestAuth("profile", "Create a free account to publish and generate your payment page QR.")
                  : downloadQrPng(liveQrUrl, `${active.slug}-payment-page-qr.png`)}
              >
                <Download size={17} />
                URL QR
              </button>
              <button
                className="button secondary"
                onClick={() => isGuest
                  ? requestAuth("profile", "Create a free account before using Pro QR tools.")
                  : isPro ? downloadBrandedQrPng(liveQrUrl, `${active.slug}-logo-qr.png`, logoDataUrl, active.businessName) : openProFeature("Logo QR studio")}
              >
                {isPro ? <Download size={17} /> : <Lock size={17} />}
                {isPro ? "Logo QR" : "Logo QR (Pro)"}
              </button>
            </div>
            <p className="fine-print">
              Lucaden creates payment pages and request links. Customers still pay directly in their bank or wallet app.
              {isGuest ? " Create a free account to publish this URL." : ""}
            </p>
          </div>
        </section>

        <section className="dashboard-section split payment-tracker-section" ref={paymentsSectionRef}>
          <form onSubmit={createRequest} className="form-panel">
            <h2>Create payment / invoice record</h2>
            <p className="fine-print">
              Save an invoice-style record, generate a dynamic payment link, and mark it received after you check your bank or wallet account.
            </p>
            <div className="form-grid">
              <Field label="Amount" name="amount" defaultValue={pendingRequestDraft?.amount || "500"} inputMode="decimal" />
              <SelectField label="Currency" name="currency" options={["HKD", "RMB"]} defaultValue={pendingRequestDraft?.currency || active.currency} />
              <Field label="Purpose" name="title" defaultValue={pendingRequestDraft?.title || "Deposit"} />
              <Field label="Product name" name="productName" defaultValue={pendingRequestDraft?.productName || "Signature bouquet"} />
              <Field label="Bundle" name="bundleName" defaultValue={pendingRequestDraft?.bundleName || "Wedding bundle"} />
              <Field label="Discount offer" name="discountOffer" defaultValue={pendingRequestDraft?.discountOffer || "10% off before Friday"} />
              <Field label="Reference" name="reference" defaultValue={pendingRequestDraft?.reference || "INV001"} />
              <Field label="Customer name" name="customerName" defaultValue={pendingRequestDraft?.customerName || ""} />
              <Field label="Customer contact" name="customerContact" defaultValue={pendingRequestDraft?.customerContact || ""} />
              <Field label="Expiry date" name="expiresAt" defaultValue={pendingRequestDraft?.expiresAt || ""} type="date" />
              <Field label="Offer details" name="offerDetails" defaultValue={pendingRequestDraft?.offerDetails || "Includes delivery and setup."} />
              <Field label="Internal note" name="internalNote" defaultValue={pendingRequestDraft?.internalNote || ""} />
            </div>
            <button className="button primary" type="submit">
              <Send size={17} />
              Create payment link
            </button>
          </form>

          <div className="form-panel">
            <div className="panel-heading">
              <h2>Payment and invoice tracker</h2>
              <button
                className="icon-button"
                title={isPro ? "Export CSV" : "CSV export is Pro"}
                onClick={() =>
                  isPro ? downloadCsv(
                    `${active.slug}-payment-requests.csv`,
                    businessRequests.map((request) => ({
                      title: request.title,
                      product: request.productName || "",
                      bundle: request.bundleName || "",
                      offer: request.discountOffer || "",
                      amount: request.amount,
                      currency: request.currency,
                      reference: request.reference,
                      status: request.status,
                      link: `${siteOrigin()}/pay/${active.slug}/request/${request.requestCode}`
                    }))
                  ) : openProFeature("CSV reconciliation export")
                }
              >
                {isPro ? <Download size={18} /> : <Lock size={18} />}
              </button>
            </div>
            <div className="payment-tracker-stats">
              <span>
                <ReceiptText size={16} />
                Records
                <strong>{businessRequests.length}</strong>
              </span>
              <span>
                <CircleDollarSign size={16} />
                Awaiting
                <strong>{openRequests.length}</strong>
              </span>
              <span>
                <BadgeCheck size={16} />
                Received
                <strong>{paidRequests.length} / {receivedDisplay}</strong>
              </span>
            </div>
            <p className="fine-print">
              Lucaden stores these payment and invoice records for your workflow. Receipt status is marked by you after checking your own FPS account.
            </p>
            <div className="request-list">
              {businessRequests.length ? businessRequests.map((request) => {
                const url = `${siteOrigin()}/pay/${active.slug}/request/${request.requestCode}`;
                const isPaid = request.status === "Merchant marked as paid";
                return (
                  <article key={request.id} className={`request-item ${isPaid ? "paid" : ""}`}>
                    <div>
                      <strong>{request.productName || request.title}</strong>
                      <span>{money(request.amount, request.currency)} - {request.discountOffer || request.reference || request.requestCode}</span>
                      <small className={`status-pill ${isPaid ? "paid" : ""}`}>
                        {isPaid ? <BadgeCheck size={14} /> : <ReceiptText size={14} />}
                        {request.status}
                      </small>
                    </div>
                    <div className="mini-actions">
                      <button title="Copy request link" onClick={() => copyText(url).then(() => notify("Request link copied"))}><LinkIcon size={16} /></button>
                      <button title={isPro ? "Download branded QR" : "Logo QR is Pro"} onClick={() => isPro ? downloadBrandedQrPng(url, `${request.requestCode}-qr.png`, logoDataUrl, request.productName || request.title) : openProFeature("Logo QR studio")}><QrCode size={16} /></button>
                      <button className={isPaid ? "paid-action" : ""} title={isPaid ? "Already marked paid" : "Mark as paid"} disabled={isPaid} onClick={() => markPaid(request.id)}>
                        {isPaid ? <BadgeCheck size={16} /> : <Check size={16} />}
                      </button>
                    </div>
                  </article>
                );
              }) : (
                <div className="empty-tracker">
                  <FileText size={34} />
                  <strong>No payment records yet</strong>
                  <span>Create a payment link to store the invoice reference, amount, customer note, and received status here.</span>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="dashboard-section split">
          <form className="form-panel" onSubmit={saveErp}>
            <h2>ERP webhook</h2>
            <p className="fine-print">
              Use a generic webhook or CSV export to bridge invoice/request data into SAP, Oracle ERP Cloud,
              Oracle NetSuite, Microsoft Dynamics 365, Xero, QuickBooks, Odoo, Shopify, WooCommerce, Zapier,
              Make, Workato, or a custom middleware flow. This sends request data only; it is not FPS payment confirmation.
            </p>
            <div className="form-grid">
              <SelectField label="Provider" name="provider" options={["Generic webhook", "SAP bridge", "Oracle ERP Cloud bridge", "Oracle NetSuite bridge", "Microsoft Dynamics 365 bridge", "Xero bridge", "QuickBooks bridge", "Odoo", "Shopify", "WooCommerce", "Zapier / Make", "Workato"]} defaultValue={erp?.provider || "Generic webhook"} />
              <Field label="Webhook URL" name="webhookUrl" defaultValue={erp?.webhook_url || ""} />
              <Field label="Shared secret / token" name="sharedSecret" defaultValue="" />
              <label><span>Enabled</span><input type="checkbox" name="enabled" defaultChecked={Boolean(erp?.enabled)} /></label>
            </div>
            <div className="action-row">
              <button className="button primary" type="submit"><PlugZap size={17} /> Save ERP webhook</button>
              <button className="button secondary" type="button" onClick={testErp}><Send size={17} /> Test ping</button>
            </div>
            <p className="fine-print">HKICL/FPS receipt confirmation needs a bank, SVF, or acquirer integration. Lucaden does not claim payment receipt from a QR scan or page view.</p>
          </form>

          <div className="form-panel">
            <h2>Pro features at HKD99/mo</h2>
            <div className="pro-list">
              <span><Crown size={17} /> Logo QR and branded payment cards</span>
              <span><ReceiptText size={17} /> Unlimited product, bundle, discount, and invoice links</span>
              <span><PlugZap size={17} /> ERP/webhook export for SAP, Oracle, NetSuite, Dynamics, Xero, Odoo, Shopify, and middleware tools</span>
              <span><Download size={17} /> CSV exports and reconciliation notes</span>
              <span><FileSpreadsheet size={17} /> Bulk link import for product lists and invoice batches</span>
              <span><MessageCircle size={17} /> WhatsApp-ready payer messages and reminder copy</span>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function AuthPage({
  reloadAuth,
  notify,
  navigate
}: {
  reloadAuth: () => Promise<void>;
  notify: (message: string) => void;
  navigate: (href: string) => void;
}) {
  return (
    <main className="auth-page">
      <section className="auth-shell">
        <div className="auth-intro">
          <p className="eyebrow">Merchant dashboard</p>
          <h1>Create your payment page.</h1>
          <p>Build an FPS-ready business profile, payment links, QR downloads, and a public payment URL from one calm workspace.</p>
          <div className="auth-points">
            <span><QrCode size={18} /> Dynamic QR links</span>
            <span><ReceiptText size={18} /> Product and offer requests</span>
            <span><Store size={18} /> Public merchant URL</span>
          </div>
        </div>

        <AuthPanel
          reloadAuth={reloadAuth}
          notify={notify}
          navigate={navigate}
          title="Create your free account"
          text="Start with Google or email. The free payment page tools stay free forever."
        />
      </section>
    </main>
  );
}

function AuthPanel({
  reloadAuth,
  notify,
  navigate,
  title,
  text,
  compact = false
}: {
  reloadAuth: () => Promise<void>;
  notify: (message: string) => void;
  navigate: (href: string) => void;
  title: string;
  text: string;
  compact?: boolean;
}) {
  const [mode, setMode] = useState<"signup" | "login">("signup");

  async function submit(endpoint: string, event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await apiJson(endpoint, {
        method: "POST",
        body: JSON.stringify({
          name: String(form.get("name") || ""),
          email: String(form.get("email") || ""),
          password: String(form.get("password") || "")
        })
      });
      await reloadAuth();
      notify("Logged in");
      navigate("/dashboard");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Authentication failed");
    }
  }

  const isSignup = mode === "signup";

  return (
    <div className={`auth-panel ${compact ? "compact-auth" : ""}`}>
      <div className="auth-panel-head">
        <h2>{isSignup ? title : "Welcome back"}</h2>
        <p>{isSignup ? text : "Log in with Google or your email password."}</p>
      </div>

      <a className="button primary full google-button" href="/api/auth/google"><UserPlus size={17} /> Continue with Google</a>

      <div className="auth-divider"><span>Email</span></div>

      <div className="segmented-control" aria-label="Email auth mode">
        <button type="button" className={isSignup ? "active" : ""} aria-pressed={isSignup} onClick={() => setMode("signup")}>Create</button>
        <button type="button" className={!isSignup ? "active" : ""} aria-pressed={!isSignup} onClick={() => setMode("login")}>Log in</button>
      </div>

      <form className="auth-form" onSubmit={(event) => submit(isSignup ? "/api/auth/register" : "/api/auth/login", event)}>
        {isSignup ? <Field label="Name / business name" name="name" defaultValue="" /> : null}
        <Field label="Email" name="email" type="email" defaultValue="" />
        <Field label="Password" name="password" type="password" defaultValue="" />
        <button className={`button ${isSignup ? "primary" : "secondary"} full`} type="submit">
          {isSignup ? "Create free account" : "Log in"}
        </button>
      </form>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: JSX.Element; label: string; value: string }) {
  return (
    <article className="stat">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function Field({
  label,
  name,
  defaultValue,
  value,
  onChange,
  type = "text",
  inputMode,
  inputRef
}: {
  label: string;
  name: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  type?: string;
  inputMode?: "decimal" | "numeric";
  inputRef?: Ref<HTMLInputElement>;
}) {
  return (
    <label>
      <span>{label}</span>
      <input
        ref={inputRef}
        name={name}
        defaultValue={value === undefined ? defaultValue : undefined}
        value={value}
        onChange={onChange ? (event) => onChange(event.currentTarget.value) : undefined}
        type={type}
        inputMode={inputMode}
      />
    </label>
  );
}

function SelectField({
  label,
  name,
  options,
  defaultValue,
  value,
  onChange
}: {
  label: string;
  name: string;
  options: string[];
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
}) {
  return (
    <label>
      <span>{label}</span>
      <select
        name={name}
        defaultValue={value === undefined ? defaultValue : undefined}
        value={value}
        onChange={onChange ? (event) => onChange(event.currentTarget.value) : undefined}
      >
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
  );
}

function ReportPage({ businesses, reports, refresh, notify, slug, navigate }: RouteContext & { slug: string }) {
  const business = businesses.find((item) => item.slug === slug);
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const report: ReportRecord = {
      id: `report_${crypto.randomUUID()}`,
      businessSlug: slug,
      reporterEmail: String(form.get("reporterEmail") || ""),
      reason: String(form.get("reason") || "Suspicious payment details"),
      description: String(form.get("description") || ""),
      status: "open",
      createdAt: new Date().toISOString()
    };
    refresh(undefined, undefined, [report, ...reports]);
    notify("Report submitted for review");
    navigate("/");
  }

  return (
    <main className="section narrow">
      <div className="page-title">
        <p className="eyebrow">Abuse prevention</p>
        <h1>Report suspicious profile</h1>
        <p>{business ? `You are reporting ${business.businessName}.` : "This profile may no longer be available."}</p>
      </div>
      <form className="form-panel" onSubmit={submit}>
        <Field label="Your email" name="reporterEmail" type="email" />
        <label>
          <span>Reason</span>
          <select name="reason">
            <option>Suspicious payment details</option>
            <option>Impersonation</option>
            <option>Wrong business information</option>
            <option>Fraud concern</option>
          </select>
        </label>
        <label>
          <span>Description</span>
          <textarea name="description" rows={5} placeholder="Add what looks wrong and any context that helps review." />
        </label>
        <button className="button primary" type="submit"><Flag size={17} /> Submit report</button>
      </form>
    </main>
  );
}

function AdminPage({ businesses, requests, reports, refresh, notify }: RouteContext) {
  function suspendBusiness(id: string) {
    const next = businesses.map((business) => (business.id === id ? { ...business, status: "suspended" as const } : business));
    refresh(next, requests, reports);
    notify("Business suspended");
  }

  return (
    <main className="section">
      <div className="page-title">
        <p className="eyebrow">Basic review console</p>
        <h1>Admin dashboard</h1>
        <p>Production should protect this route with admin authentication, sensitive-data access logs, and least-privilege controls.</p>
      </div>
      <section className="admin-grid">
        <div className="form-panel">
          <h2>Businesses</h2>
          {businesses.map((business) => (
            <article className="request-item" key={business.id}>
              <div>
                <strong>{business.businessName}</strong>
                <span>{business.slug} - {business.status} - {verificationLabel(business.verificationLevel)}</span>
                <small>{business.fpsProxyType}: {maskFps(business.fpsProxyValue)}</small>
              </div>
              <button title="Suspend business" onClick={() => suspendBusiness(business.id)}><Ban size={16} /></button>
            </article>
          ))}
        </div>
        <div className="form-panel">
          <h2>Reports</h2>
          {reports.length ? reports.map((report) => (
            <article className="request-item" key={report.id}>
              <div>
                <strong>{report.reason}</strong>
                <span>{report.businessSlug} - {report.status}</span>
                <small>{report.description || "No description"}</small>
              </div>
            </article>
          )) : <p className="fine-print">No reports yet.</p>}
        </div>
      </section>
    </main>
  );
}

function AuthorPill({ author }: { author: AuthorProfile }) {
  return (
    <div className="author-pill">
      <img src={author.avatar} alt="" loading="lazy" />
      <span>By {author.name}</span>
    </div>
  );
}

function AuthorCard({ author }: { author: AuthorProfile }) {
  return (
    <section className="author-card">
      <img src={author.avatar} alt="" loading="lazy" />
      <div>
        <span>Author</span>
        <h2>{author.name}</h2>
        <strong>{author.role}</strong>
        <p>{author.bio}</p>
      </div>
    </section>
  );
}

function BlogShareBar({ title, url }: { title: string; url: string }) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const shareText = encodeURIComponent(`${title} ${url}`);

  function openShare(href: string) {
    window.open(href, "_blank", "noopener,noreferrer,width=720,height=640");
  }

  async function nativeShare() {
    if (navigator.share) {
      await navigator.share({ title, url }).catch(() => undefined);
      return;
    }
    await copyText(url);
  }

  return (
    <div className="share-bar" aria-label="Share this article">
      <button onClick={nativeShare}><Share2 size={16} /> Share</button>
      <button onClick={() => openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`)}>LinkedIn</button>
      <button onClick={() => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)}>Facebook</button>
      <button onClick={() => openShare(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`)}>X</button>
      <button onClick={() => openShare(`https://wa.me/?text=${shareText}`)}><MessageCircle size={16} /> WhatsApp</button>
      <a href={`mailto:?subject=${encodedTitle}&body=${shareText}`}><Mail size={16} /> Email</a>
      <button onClick={() => copyText(url)}><LinkIcon size={16} /> Copy</button>
    </div>
  );
}

function BlogIndexPage({ navigate }: { navigate: (href: string) => void }) {
  return (
    <main className="section">
      <div className="page-title">
        <p className="eyebrow">FPS guides</p>
        <h1>Practical Hong Kong FPS setup guides for shops, SMEs, and service teams.</h1>
        <p>Focused long-tail guides for receiving FPS payments, using payment page links, collecting references, and reducing customer payment mistakes.</p>
      </div>
      <div className="blog-grid">
        {blogPosts.map((post) => {
          const author = authorForSlug(post.slug);
          return (
            <article className="blog-card" key={post.slug}>
              <span className="badge green">{post.audience}</span>
              <h2>{post.title}</h2>
              <AuthorPill author={author} />
              <p>{post.description}</p>
              <div className="blog-meta">
                <span><Search size={14} /> {post.keyword}</span>
                <span><BookOpen size={14} /> {post.readTime}</span>
              </div>
              <div className="official-link-row">
                {post.sources.slice(0, 2).map((source) => (
                  <a key={source.url} href={source.url} target="_blank" rel="noreferrer">
                    <ExternalLink size={14} />
                    {source.title}
                  </a>
                ))}
              </div>
              <button className="inline-link" onClick={() => navigate(`/blog/${post.slug}`)}>
                Read guide <ArrowRight size={15} />
              </button>
            </article>
          );
        })}
      </div>
    </main>
  );
}

function BlogPostPage({ slug, navigate }: { slug: string; navigate: (href: string) => void }) {
  const post = blogPosts.find((item) => item.slug === slug);
  if (!post) {
    return (
      <main className="section narrow">
        <div className="empty-state">
          <BookOpen size={42} />
          <h1>Guide not found</h1>
          <button className="button primary" onClick={() => navigate("/blog")}>Back to guides</button>
        </div>
      </main>
    );
  }
  const author = authorForSlug(post.slug);
  const currentIndex = blogPosts.findIndex((item) => item.slug === post.slug);
  const relatedPosts = [...blogPosts.slice(currentIndex + 1), ...blogPosts.slice(0, currentIndex)]
    .filter((item) => item.slug !== post.slug)
    .slice(0, 3);
  const shareUrl = `${siteOrigin()}/blog/${post.slug}`;

  return (
    <main className="section blog-post-shell">
      <article className="blog-post">
        <button className="inline-link" onClick={() => navigate("/blog")}><ArrowRight className="back-icon" size={15} /> All guides</button>
        <p className="eyebrow">{post.audience}</p>
        <h1>{post.title}</h1>
        <p className="post-description">{post.description}</p>
        <div className="blog-meta">
          <span><Search size={14} /> {post.keyword}</span>
          <span><BookOpen size={14} /> {post.readTime}</span>
          <span>{post.published}</span>
        </div>
        <AuthorCard author={author} />
        <BlogShareBar title={post.title} url={shareUrl} />
        {post.sections.map((section) => (
          <section key={section.heading}>
            <h2>{section.heading}</h2>
            {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </section>
        ))}
        <section className="source-box">
          <h2>Sources checked</h2>
          {post.sources.map((source) => (
            <a key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.title}</a>
          ))}
        </section>
        <section className="related-guides">
          <h2>Related guides</h2>
          {relatedPosts.map((related) => (
            <button key={related.slug} onClick={() => navigate(`/blog/${related.slug}`)}>
              <span>{related.audience}</span>
              <strong>{related.title}</strong>
            </button>
          ))}
        </section>
        <div className="post-cta">
          <h2>Create your own FPS payment page</h2>
          <p>The free Lucaden profile gives you a dedicated payment URL, URL QR, and customer instructions. Pro adds branded QR, bulk links, exports, and integrations.</p>
          <button className="button primary" onClick={() => navigate("/dashboard")}><Rocket size={17} /> Start now</button>
        </div>
      </article>
    </main>
  );
}

function FaqPage({ navigate }: { navigate: (href: string) => void }) {
  const faqs = [
    ["What is Lucaden?", "Lucaden is an independent Hong Kong FPS payment page builder. It helps a business turn its FPS receiving details into a clean public profile URL, QR code, and payment request workflow."],
    ["What do you do?", "We provide the page, QR, copy buttons, payer instructions, payment request links, reference fields, and optional business workflow tools. The actual FPS transfer still happens in the payer's own bank or wallet app."],
    ["Why did you develop this tool?", "Lucaden was built for people who do not have spare developer time, IT budget, or payment-integration resources. A merchant can create a static FPS profile QR for everyday use, then generate dynamic QR links with amounts, references, products, deposits, or invoice notes whenever a specific payment needs more context."],
    ["Can I use my Lucaden link as a site profile URL?", "Yes. Your Lucaden link can act as a dedicated payment profile URL for invoices, WhatsApp, Instagram, email signatures, quotations, receipts, or a website payment button."],
    ["Where does the money go?", "Payments are sent directly from the customer to the business owner's FPS-linked bank or wallet account. Lucaden does not touch, hold, settle, or route the money."],
    ["How much does Lucaden charge per payment?", "Nothing. Lucaden charges 0% transaction fee. We do not take a cut from FPS transfers."],
    ["Will the free features stay free?", "Yes. The free payment profile, basic URL QR, basic payment links, payer instructions, and direct-payment workflow are intended to stay free forever."],
    ["How does Lucaden operate as a business?", "We operate with an optional HKD99/mo plan for users who want extra tools such as branded QR output, unlimited request workflows, exports, reminders, team workflows, and integrations. The paid plan funds the free core product."],
    ["Do you process payments or confirm receipt?", "No. Lucaden is not a bank, stored value facility, payment processor, FPS operator, or receipt-confirmation provider. Merchants should check their own bank or wallet account and mark requests manually unless they later connect their own external system."],
    ["Is this an official FPS, HKICL, or HKMA service?", "No. Lucaden is independent and must not be presented as an official FPS, HKICL, HKMA, bank-approved, or bank-verified service."],
    ["What should customers check before paying?", "Customers should check the recipient name shown in their bank or wallet app, the amount, the FPS proxy, and the reference before confirming. FPS transfers are fast, so review details carefully."],
    ["Can merchants hide phone or email?", "Yes. Profile controls include masking raw FPS proxy details and disabling the copy button."]
  ];
  return (
    <main className="section narrow">
      <div className="page-title">
        <p className="eyebrow">Help center</p>
        <h1>How Lucaden helps businesses create free FPS payment pages and QR links.</h1>
      </div>
      <div className="faq-list">
        {faqs.map(([question, answer]) => (
          <article key={question}>
            <h2>{question}</h2>
            <p>{answer}</p>
          </article>
        ))}
      </div>
      <button className="button primary" onClick={() => navigate("/dashboard")}><Rocket size={17} /> Start now</button>
    </main>
  );
}

function PricingPage({ navigate }: { navigate: (href: string) => void }) {
  const freeFeatures = [
    "Dedicated FPS payment profile URL",
    "Basic URL QR download",
    "Basic amount and reference payment links",
    "Copyable FPS details and payer instructions",
    "Mask raw FPS proxy details when needed",
    "Direct-to-owner FPS payments with 0% Lucaden transaction fee",
    "Free forever core workflow"
  ];
  const proFeatures = [
    "Branded QR downloads with business logo",
    "Unlimited product, bundle, invoice, deposit, and discount links",
    "Customer name, contact, expiry, and internal note fields",
    "CSV exports for reconciliation and accountant handoff",
    "Bulk payment link creation for product lists and invoice batches",
    "WhatsApp-ready payment messages and reminder copy",
    "Reusable payment templates for deposits, balances, subscriptions, and COD",
    "Branded payment cards for quotes, invoices, and social shops",
    "ERP/webhook export for Xero, Odoo, Shopify, WooCommerce, Zapier, or Make bridges",
    "Staff workflow for safer link creation and fewer manual mistakes",
    "Request status tracking and merchant-paid marking",
    "Priority product support for setup and workflow questions"
  ];
  const reasons = [
    ["Save vendor time", "Avoid building a small FPS payment page from scratch every time a shop, branch, or campaign needs one."],
    ["Reduce payment mistakes", "Show amount, reference, recipient expectation, payer notes, and copy buttons in one focused flow."],
    ["Move faster on invoices", "Generate links for deposits, balances, product bundles, and one-off customer requests without waiting for IT."],
    ["Make FPS feel professional", "Share a clean dedicated profile URL instead of a bare phone number, FPS ID, or screenshot."],
    ["Keep the direct-payment advantage", "Customers still pay straight to the business owner, and Lucaden still takes 0% of the transfer."]
  ];
  return (
    <main className="section">
      <div className="page-title">
        <p className="eyebrow">Plans</p>
        <h1>Free FPS payment pages forever. HKD99/mo for serious payment operations.</h1>
        <p>The free product stays free. Pro exists for teams that want branding, exports, bulk workflows, reminders, and integrations.</p>
      </div>
      <div className="pricing-grid two-prices">
        <article className="price-card">
          <h2>Free</h2>
          <strong>HKD0</strong>
          <p>For any business that needs a clean FPS payment page and direct-payment QR workflow.</p>
          <div className="price-feature-list">
            {freeFeatures.map((feature) => <span key={feature}><Check size={16} /> {feature}</span>)}
          </div>
          <button className="button secondary full" onClick={() => navigate("/dashboard")}>Start free</button>
        </article>
        <article className="price-card highlight-price">
          <div className="badge green">Best for growing teams</div>
          <h2>Pro</h2>
          <strong>HKD99/mo</strong>
          <p>For shops, service teams, and SMEs that send payment requests often and want a more polished workflow.</p>
          <div className="price-feature-list">
            {proFeatures.map((feature) => <span key={feature}><Sparkles size={16} /> {feature}</span>)}
          </div>
          <button className="button primary full" onClick={() => navigate("/dashboard")}>Start with Pro tools</button>
        </article>
      </div>

      <section className="section compact-section">
        <div className="section-heading">
          <p className="eyebrow">Why HKD99/mo is worth it</p>
          <h2>More than a QR code. A small payment operations desk.</h2>
        </div>
        <div className="reason-grid">
          {reasons.map(([title, text]) => (
            <article className="feature" key={title}>
              <ListChecks />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

export default App;
